import React, { useMemo, useState, createElement } from 'react';
import { FileText, Download, ClipboardCheck, ArrowRight, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { MODULES } from '../../../content';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { StudentReportCard } from '../components/StudentReportCard';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { downloadCsv } from '../../../utils/csvExport';
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
interface ReportsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  onViewAssessmentResults?: () => void;
}
const PERIODS = [{
  key: 7,
  label: 'week'
}, {
  key: 30,
  label: 'maand'
}, {
  key: 90,
  label: 'trimester'
}, {
  key: 365,
  label: 'jaar'
}];
function getPeriodStats(questionStates: Record<string, any>, familyMastery: Record<string, any>, group: number, periodDays: number) {
  const now = Date.now();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const periodMs = periodDays * MS_PER_DAY;
  const currentStart = now - periodMs;
  const previousStart = currentStart - periodMs;
  const uniqueDays = new Set<string>();
  for (const state of Object.values(questionStates || {})) {
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        const dateStr = new Date(attempt.timestamp).toISOString().split('T')[0];
        uniqueDays.add(dateStr);
      }
    }
  }
  let currentCorrect = 0;
  let currentTotal = 0;
  let previousCorrect = 0;
  let previousTotal = 0;
  for (const [key, state] of Object.entries(familyMastery || {})) {
    if (!key.includes(`_g${group}`)) continue;
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        currentTotal++;
        if (attempt.isCorrect) currentCorrect++;
      } else if (attempt.timestamp >= previousStart && attempt.timestamp < currentStart) {
        previousTotal++;
        if (attempt.isCorrect) previousCorrect++;
      }
    }
  }
  let trendDelta = 0;
  if (currentTotal >= 3 && previousTotal >= 3) {
    const currentAcc = currentCorrect / currentTotal;
    const previousAcc = previousCorrect / previousTotal;
    trendDelta = Math.round((currentAcc - previousAcc) * 100);
  }
  return {
    daysPracticed: uniqueDays.size,
    trendDelta
  };
}
export function ReportsPage({
  students,
  selectedGroup,
  getModuleMastery,
  onNavigate,
  onViewAssessmentResults
}: ReportsPageProps) {
  const [periodDays, setPeriodDays] = useState<number>(7);
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const {
        daysPracticed,
        trendDelta
      } = getPeriodStats(student.data.questionStates || {}, student.data.familyMastery || {}, selectedGroup, periodDays);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        daysPracticed,
        trendDelta
      };
    });
  }, [students, getModuleMastery, selectedGroup, periodDays]);
  const moduleStats = useMemo(() => {
    return getClassModuleStats(students, selectedGroup, getModuleMastery);
  }, [students, selectedGroup, getModuleMastery]);
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return {
      classAvg: 0,
      activeThisPeriod: 0
    };
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    const activeThisPeriod = enrichedStudents.filter(s => s.daysPracticed > 0).length;
    return {
      classAvg,
      activeThisPeriod
    };
  }, [enrichedStudents]);
  const activePeriodLabel = PERIODS.find(p => p.key === periodDays)?.label || 'week';
  const handleExportClassListCsv = () => {
    const headers = ['Naam', 'Gemiddelde Beheersing (%)', 'Trend (%)', `Dagen Geoefend (deze ${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak'];
    const rows = enrichedStudents.map(s => {
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'klassenlijst');
  };
  const handleExportSubjectsCsv = () => {
    const headers = ['Vak', 'Klasgemiddelde (%)', 'Leerlingen Geoefend', 'Trend'];
    const rows = moduleStats.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.moduleTitle}"`, m.avgMastery, m.practicedCount, `"${trendText}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'vakken_overzicht');
  };
  return <PageLayout title="Rapporten" description={`${summaryStats.activeThisPeriod}/${students.length} leerlingen actief deze ${activePeriodLabel}`} data-id="element-5860">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5861">
        {/* Summary Strip */}
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-5862">
          <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-5863">
            <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5864">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5865">
                <FileText className="w-5 h-5" data-id="element-5866" />
              </div>
              <div data-id="element-5867">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5868">
                  Klasgemiddelde
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5869">
                  {summaryStats.classAvg}%
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-5870">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 shadow-inner" data-id="element-5871">
                <ArrowUpRight className="w-5 h-5" data-id="element-5872" />
              </div>
              <div data-id="element-5873">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5874">
                  Actief deze {activePeriodLabel}
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5875">
                  {summaryStats.activeThisPeriod}{' '}
                  <span className="text-sm font-medium text-slate-500" data-id="element-5876">
                    / {students.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Period Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-5877">
          {PERIODS.map(period => {
          const isActive = periodDays === period.key;
          return <button key={period.key} onClick={() => setPeriodDays(period.key)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'}`} data-id="element-5878">
                Deze {period.label}
              </button>;
        })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5879">
          {/* Klas-overzicht Section */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col" data-id="element-5880">
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-3 border border-indigo-100" data-id="element-5881">
              <FileText className="w-6 h-6" data-id="element-5882" />
            </div>
            <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-5883">
              Klas-overzicht
            </h2>
            <p className="text-slate-500 mb-5 flex-1" data-id="element-5884">
              Exporteer de huidige status van de hele klas. Handig voor je eigen
              administratie of om te bespreken met collega's.
            </p>

            <div className="space-y-3" data-id="element-5885">
              <Button onClick={handleExportClassListCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5886">
                <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-5887" />
                <div data-id="element-5888">
                  <div className="font-bold text-slate-900" data-id="element-5889">
                    Klassenlijst CSV
                  </div>
                  <div className="text-xs text-slate-500 font-normal" data-id="element-5890">
                    Leerlingen, gemiddelde beheersing, activiteit
                  </div>
                </div>
              </Button>

              <Button onClick={handleExportSubjectsCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5891">
                <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-5892" />
                <div data-id="element-5893">
                  <div className="font-bold text-slate-900" data-id="element-5894">
                    Vakken-overzicht CSV
                  </div>
                  <div className="text-xs text-slate-500 font-normal" data-id="element-5895">
                    Klasgemiddelde per vak, trends
                  </div>
                </div>
              </Button>
            </div>
          </Card>

          {/* Per-leerling rapport Section */}
          <StudentReportCard students={enrichedStudents} selectedGroup={selectedGroup} getModuleMastery={getModuleMastery} periodDays={periodDays} data-id="element-5896" />
        </div>

        {/* Week-over-week Vergelijking */}
        {enrichedStudents.length > 0 && <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-5897">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between" data-id="element-5898">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5899">
                Periode vergelijking
              </h3>
              <span className="text-[10px] text-slate-400 font-medium" data-id="element-5900">
                Deze {activePeriodLabel} vs. vorige {activePeriodLabel}
              </span>
            </div>
            {(() => {
          const sorted = [...enrichedStudents].sort((a, b) => b.trendDelta - a.trendDelta);
          const improvers = sorted.filter(s => s.trendDelta > 0);
          const stable = sorted.filter(s => s.trendDelta === 0);
          const decliners = sorted.filter(s => s.trendDelta < 0);
          const renderRow = (student: (typeof enrichedStudents)[0], i: number, bgAlt: boolean) => <div key={student.id} className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center px-4 py-2.5 ${bgAlt ? 'bg-slate-50/30' : 'bg-white'}`} data-id="element-5901">
                  <div className="font-bold text-sm text-slate-900 truncate" data-id="element-5902">
                    {student.name}
                  </div>
                  <div className={`font-black text-sm tabular-nums ${student.avgMastery >= 70 ? 'text-emerald-600' : student.avgMastery >= 40 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-5903">
                    {student.avgMastery}%
                  </div>
                  <div className="flex items-center gap-1 min-w-[60px] justify-end" data-id="element-5904">
                    {student.trendDelta > 0 && <>
                        <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" data-id="element-5905" />
                        <span className="text-xs font-bold text-emerald-600 tabular-nums" data-id="element-5906">
                          +{student.trendDelta}%
                        </span>
                      </>}
                    {student.trendDelta < 0 && <>
                        <ArrowDownRight className="w-3.5 h-3.5 text-rose-500" data-id="element-5907" />
                        <span className="text-xs font-bold text-rose-600 tabular-nums" data-id="element-5908">
                          {student.trendDelta}%
                        </span>
                      </>}
                    {student.trendDelta === 0 && <span className="text-xs font-medium text-slate-400 tabular-nums" data-id="element-5909">
                        —
                      </span>}
                  </div>
                </div>;
          return <div data-id="element-5910">
                  {improvers.length > 0 && <>
                      <div className="px-4 py-2 bg-emerald-50/80 border-y border-emerald-100 flex items-center gap-2" data-id="element-5911">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-5912">
                          <ArrowUpRight className="w-3.5 h-3.5" data-id="element-5913" />
                        </div>
                        <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider" data-id="element-5914">
                          Stijgers ({improvers.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5915">
                        {improvers.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                  {stable.length > 0 && <>
                      <div className="px-4 py-2 bg-slate-50 border-y border-slate-100 flex items-center gap-2" data-id="element-5916">
                        <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center" data-id="element-5917">
                          <ArrowRight className="w-3.5 h-3.5" data-id="element-5918" />
                        </div>
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider" data-id="element-5919">
                          Stabiel ({stable.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5920">
                        {stable.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                  {decliners.length > 0 && <>
                      <div className="px-4 py-2 bg-rose-50/80 border-y border-rose-100 flex items-center gap-2" data-id="element-5921">
                        <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-5922">
                          <ArrowDownRight className="w-3.5 h-3.5" data-id="element-5923" />
                        </div>
                        <span className="text-xs font-bold text-rose-800 uppercase tracking-wider" data-id="element-5924">
                          Dalers ({decliners.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5925">
                        {decliners.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                </div>;
        })()}
          </Card>}
      </div>

      {/* Cross-link to Toetsen Resultaten */}
      {(onViewAssessmentResults || onNavigate) && <motion.div variants={itemVariants} className="max-w-5xl mx-auto mt-8" data-id="element-5926">
          <Card className="p-6 border-orange-200 bg-gradient-to-br from-orange-50/50 to-orange-100/30 flex flex-col sm:flex-row items-center gap-6" data-id="element-5927">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border border-orange-200 shadow-sm" data-id="element-5928">
              <ClipboardCheck className="w-6 h-6 text-orange-500" data-id="element-5929" />
            </div>
            <div className="flex-1 text-center sm:text-left" data-id="element-5930">
              <h3 className="text-lg font-black text-slate-900 mb-1" data-id="element-5931">
                Toetsresultaten bekijken
              </h3>
              <p className="text-sm text-slate-600" data-id="element-5932">
                Bekijk gedetailleerde toetsresultaten en niveaubepalingen van je
                leerlingen.
              </p>
            </div>
            <Button onClick={() => onViewAssessmentResults ? onViewAssessmentResults() : onNavigate?.('assessments')} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm shrink-0" data-id="element-5933">
              <ArrowRight className="w-4 h-4 mr-2" data-id="element-5934" />
              Naar Toetsresultaten
            </Button>
          </Card>
        </motion.div>}
    </PageLayout>;
}